import { ReactElement } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import { isUndefined } from 'lodash-es'
import numeral from 'numeral'
import CopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Filter2Icon from '@mui/icons-material/Filter2'
import { TableCell, TableRow, IconButton, Select, MenuItem, Tooltip } from '@mui/material'
import type { ProjectConnectionUsageDTO } from '@api/fastAPI.schemas'
import { AlertDialog } from '@ui/feedback'
import { Box } from '@ui/structure'
import { deleteConfirmationText, deleteConfirmationTitle } from '../utils/deleteConfirmation'

interface Props {
  connection: Connection
  onDelete: () => void
  onEdit: () => void
  onCopy: () => void
  onDuplicate: () => void
  onChangeOrder?: (order: number | undefined) => void
  order?: number | undefined
  showOrderInput?: boolean
  isProjectLibrary?: boolean
  /** How many anchors use this Zugverbinder (project library only). */
  usage?: ProjectConnectionUsageDTO
  /** Shows the usage column, also while the usage is still loading. */
  showUsage?: boolean
}

const ConnectionListItem = ({
  connection,
  onDelete,
  onEdit,
  onCopy,
  onDuplicate,
  onChangeOrder,
  order,
  showOrderInput = false,
  isProjectLibrary = false,
  usage,
  showUsage = false,
}: Props): ReactElement => {
  const { name, short_name, guid, export_comment, r_d_wind, r_d_earthquake } = connection
  // Applies only to the organisation's library (project connections belong to
  // the project, and every member may edit those): reading stays, everything
  // that would write is not offered to a member who is not an admin.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const readOnly = !showOrderInput && !isProjectLibrary && !isOrganisationAdmin

  return (
    <>
      <TableRow
        key={guid}
        sx={({ palette }) => ({
          '&:hover': {
            background: palette.grey[100],
          },
          cursor: 'pointer',
        })}
      >
        {showOrderInput && (
          <TableCell component="th" scope="row">
            <Select
              value={isUndefined(order) ? 'not-relevant' : order}
              onChange={e =>
                onChangeOrder?.(
                  e.target.value === 'not-relevant' ? undefined : (e.target.value as number),
                )
              }
              size="small"
            >
              {[...Array(9)].map((_, num) => {
                const order = num + 1
                return (
                  <MenuItem key={order} value={order}>
                    {order}
                  </MenuItem>
                )
              })}
              <MenuItem value="not-relevant">-</MenuItem>
            </Select>
          </TableCell>
        )}
        <TableCell>{name}</TableCell>
        <TableCell>{short_name}</TableCell>
        <TableCell>{export_comment}</TableCell>
        <TableCell>{numeral(r_d_earthquake / 1000).format('0,0.00a')}</TableCell>
        <TableCell>{numeral(r_d_wind / 1000).format('0,0.00a')}</TableCell>
        {showUsage && (
          <TableCell data-cy="connection-usage">
            {/* The anchor count needs computed anchor checks; the manual count is always known. */}
            {usage ? `${usage.anchorCount ?? '–'} (${usage.userDefinedCount})` : '–'}
          </TableCell>
        )}
        <TableCell align="right">
          <Box display="flex" justifyContent="flex-end">
            {!readOnly && (
              <AlertDialog
                title="Zugverbindung verändern?"
                text={
                  showOrderInput || isProjectLibrary
                    ? 'Änderungen an dieser Zugverbindung werden nur innerhalb des Projektes angewandt und wirken sich nicht auf andere Projekte aus.'
                    : 'Die Änderung an der Zugverbindung wird nicht in bestehende Projekte übernommen. Die Zugverbindungen müssen <b>jeweils</b> in den entsprechenden Projekten im Zugverbindungs-Manager geändert werden.'
                }
                onConfirm={onEdit}
              >
                <IconButton>
                  <EditIcon fontSize="small" />
                </IconButton>
              </AlertDialog>
            )}
            {!readOnly && (
              <Tooltip title="Duplizieren" enterDelay={500}>
                <IconButton onClick={onDuplicate} data-cy="duplicate-connection">
                  <Filter2Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="In Zwischenablage kopieren" enterDelay={500}>
              <IconButton onClick={onCopy} data-cy="copy-connection">
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!readOnly && (
              <AlertDialog
                title={deleteConfirmationTitle(usage)}
                text={deleteConfirmationText(usage)}
                onConfirm={async () => onDelete()}
              >
                <IconButton>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </AlertDialog>
            )}
          </Box>
        </TableCell>
      </TableRow>
    </>
  )
}

export default ConnectionListItem
